<template>
  <div class="wizard-overlay" :class="{ open: show }" @click.self="$emit('close')">
    <div v-if="show" class="wizard-modal">
      <!-- Header -->
      <div class="wizard-modal-header">
        <div class="wizard-modal-title">
          <i class="bx bx-cog" style="color: #8d92a9;"></i>
          {{ isEditing ? 'Editar Integração' : 'Nova Integração' }}
        </div>
        <button class="wizard-modal-close" @click="$emit('close')">
          <i class="bx bx-x" style="font-size: 18px;"></i>
        </button>
      </div>

      <!-- Steps Bar -->
      <div class="wizard-steps-bar">
        <template v-for="(step, idx) in activeSteps">
          <div
            :key="'step-' + idx"
            class="ws-item"
            :class="{ active: currentStep === idx, completed: currentStep > idx }"
            @click="goToStep(idx)"
          >
            <div class="ws-num">
              <i v-if="currentStep > idx" class="bx bx-check" style="font-size: 13px;"></i>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            {{ step }}
          </div>
          <div
            v-if="idx < activeSteps.length - 1"
            :key="'conn-' + idx"
            class="ws-connector"
            :class="{ done: currentStep > idx }"
          ></div>
        </template>
      </div>

      <!-- Body -->
      <div class="wizard-modal-body">
        <!-- Step 1: Configuração -->
        <div class="wizard-panel" :class="{ active: currentStep === 0 }">
          <div class="panel-section">
            <div class="panel-section-title">
              <i class="bx bx-cog" style="font-size: 15px;"></i> Geral
            </div>
            <div class="form-row" :style="isPullProvider ? 'grid-template-columns: 1fr' : ''">
              <div class="form-group">
                <label class="form-label">Nome da integração <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-input"
                  v-model="form.name"
                  placeholder="Ex: Sync Confirmados"
                />
              </div>
              <div v-if="!isPullProvider" class="form-group">
                <label class="form-label">Frequência</label>
                <select class="form-select" v-model="freqType" @change="onFreqTypeChange">
                  <option value="manual">Manual</option>
                  <option value="minutes">A cada X minutos</option>
                  <option value="hours">A cada X horas</option>
                  <option value="daily">Diariamente</option>
                </select>
              </div>
            </div>
            <!-- Frequency sub-input (inline row below) -->
            <div v-if="!isPullProvider && freqType !== 'manual'" class="freq-detail-row">
              <template v-if="freqType === 'minutes'">
                <span class="freq-label">Sincronizar a cada</span>
                <input
                  type="number"
                  class="form-input freq-number"
                  v-model.number="freqValue"
                  min="1"
                  max="59"
                  @input="buildFrequency"
                />
                <span class="freq-label">minutos</span>
              </template>
              <template v-if="freqType === 'hours'">
                <span class="freq-label">Sincronizar a cada</span>
                <input
                  type="number"
                  class="form-input freq-number"
                  v-model.number="freqValue"
                  min="1"
                  max="23"
                  @input="buildFrequency"
                />
                <span class="freq-label">hora(s)</span>
              </template>
              <template v-if="freqType === 'daily'">
                <span class="freq-label">Sincronizar diariamente às</span>
                <input
                  type="time"
                  class="form-input freq-time"
                  v-model="freqTime"
                  @input="buildFrequency"
                />
              </template>
            </div>
          </div>

          <div class="panel-section" v-if="selectedProvider">
            <div class="panel-section-title">
              <img
                v-if="providerLogo"
                :src="providerLogo"
                style="width: 16px; height: 16px;"
              />
              <i v-else class="bx bx-plug" style="font-size: 15px;"></i>
              {{ selectedProvider.name }}
            </div>
            <div class="form-row">
              <div
                v-for="field in inputConfigFields"
                :key="field.key"
                class="form-group"
                :style="field.full_width ? 'grid-column: 1 / -1' : ''"
              >
                <label class="form-label">
                  {{ field.label }}
                  <span v-if="field.required" class="text-danger">*</span>
                </label>
                <!-- Select type -->
                <select
                  v-if="field.type === 'select'"
                  class="form-select"
                  v-model="form.config[field.key]"
                >
                  <option
                    v-for="opt in field.options"
                    :key="opt.value"
                    :value="opt.value"
                  >{{ opt.label }}</option>
                </select>
                <!-- Textarea type -->
                <textarea
                  v-else-if="field.type === 'textarea'"
                  class="form-input"
                  v-model="form.config[field.key]"
                  :placeholder="field.placeholder || ''"
                  rows="4"
                  style="resize: vertical;"
                ></textarea>
                <!-- Text type -->
                <input
                  v-else
                  type="text"
                  class="form-input"
                  :class="{ mono: field.key === 'spreadsheet_id' }"
                  v-model="form.config[field.key]"
                  :placeholder="field.placeholder || ''"
                />
                <span v-if="field.help" class="form-hint">{{ field.help }}</span>
              </div>
            </div>
            <div
              v-for="field in boolConfigFields"
              :key="field.key"
              class="toggle-row"
            >
              <label class="toggle-switch">
                <input type="checkbox" v-model="form.config[field.key]" />
                <span class="toggle-slider"></span>
              </label>
              <span class="form-label" style="margin: 0;">{{ field.label }}</span>
            </div>

            <!-- Credential fields (ex: secret HMAC) -->
            <div v-if="credentialFields.length" class="form-row" style="margin-top: 4px;">
              <div
                v-for="field in credentialFields"
                :key="field.key"
                class="form-group"
                style="grid-column: 1 / -1;"
              >
                <label class="form-label">
                  {{ field.label }}
                  <span v-if="field.required" class="text-danger">*</span>
                </label>
                <input
                  :type="field.type === 'password' ? 'password' : 'text'"
                  class="form-input"
                  v-model="form.credentials[field.key]"
                  :placeholder="field.placeholder || ''"
                  autocomplete="new-password"
                />
                <span v-if="field.help" class="form-hint">{{ field.help }}</span>
              </div>
            </div>

            <!-- Test Connection -->
            <div v-if="isEditing" style="margin-top: 12px;">
              <div v-if="testResult === true" class="test-connection">
                <i class="bx bx-check-circle" style="font-size: 22px; color: #34c38f; flex-shrink: 0;"></i>
                <div style="flex: 1;">
                  <div class="test-connection-text">Conexão OK</div>
                  <div class="test-connection-sub">Serviço acessível e pronto para sincronização.</div>
                </div>
              </div>
              <div v-else-if="testResult === false" class="test-error-box">
                <i class="bx bx-x-circle" style="font-size: 22px; color: #f46a6a; flex-shrink: 0;"></i>
                <div style="flex: 1;">
                  <div class="test-error-text">Falha na conexão</div>
                  <div class="test-error-sub">{{ testError }}</div>
                </div>
              </div>
              <button
                v-if="testResult === null"
                class="wz-btn wz-btn-outline wz-btn-sm"
                :disabled="testing"
                @click="$emit('test')"
              >
                <i class="bx" :class="testing ? 'bx-loader-alt bx-spin' : 'bx-plug'" style="font-size: 16px;"></i>
                {{ testing ? 'Testando...' : 'Testar Conexão' }}
              </button>
              <button
                v-else
                class="wz-btn wz-btn-outline wz-btn-sm"
                style="margin-top: 8px;"
                :disabled="testing"
                @click="$emit('test')"
              >
                <i class="bx" :class="testing ? 'bx-loader-alt bx-spin' : 'bx-sync'" style="font-size: 16px;"></i>
                {{ testing ? 'Testando...' : 'Testar novamente' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Filtros (only for push providers) -->
        <div v-if="!isPullProvider" class="wizard-panel" :class="{ active: currentStep === 1 }">
          <!-- Coleta de Lead: o recorte é por período, não por status/grupo -->
          <div v-if="isLeadCollection" class="panel-section">
            <div class="panel-section-title">
              <i class="bx bx-filter-alt" style="font-size: 15px;"></i> Período da coleta
            </div>
            <p class="panel-hint">Sem preenchimento = todas as coletas realizadas.</p>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">De</label>
                <input type="date" class="form-input" v-model="form.filters.date_from" />
              </div>
              <div class="form-group">
                <label class="form-label">Até</label>
                <input type="date" class="form-input" v-model="form.filters.date_to" />
              </div>
            </div>
          </div>

          <div v-if="!isLeadCollection" class="panel-section">
            <div class="panel-section-title">
              <i class="bx bx-filter-alt" style="font-size: 15px;"></i> Filtrar por Status
            </div>
            <p class="panel-hint">Sem seleção = todos os participantes. Inclua <strong>Convidado</strong> para levar quem ainda não respondeu (base completa).</p>
            <div class="chip-presets">
              <button type="button" class="chip-preset" @click="selectAllStatuses">
                <i class="bx bx-list-check"></i> Base completa (convidados + inscritos)
              </button>
              <button v-if="form.filters.statuses.length" type="button" class="chip-preset chip-preset-clear" @click="clearStatuses">Limpar seleção</button>
            </div>
            <div class="chip-group">
              <div
                v-for="status in statusOptions"
                :key="status.value"
                class="chip"
                :class="{ selected: form.filters.statuses.includes(status.value) }"
                @click="toggleStatus(status.value)"
              >
                <span class="chip-check">
                  <i class="bx bx-check" style="font-size: 10px;"></i>
                </span>
                <span class="chip-dot" :style="{ background: status.color }"></span>
                {{ status.label }}
              </div>
            </div>
          </div>

          <div class="panel-section" v-if="groups.length && !isLeadCollection">
            <div class="panel-section-title">
              <i class="bx bx-group" style="font-size: 15px;"></i> Filtrar por Grupo
            </div>
            <p class="panel-hint">Sem seleção = todos os grupos.</p>
            <div class="chip-group">
              <div
                v-for="group in groups"
                :key="group.id"
                class="chip"
                :class="{ selected: (form.filters.group_ids || []).includes(group.id) }"
                @click="toggleGroup(group.id)"
              >
                <span class="chip-check">
                  <i class="bx bx-check" style="font-size: 10px;"></i>
                </span>
                {{ group.name }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Mapeamento (only for push providers) -->
        <div v-if="!isPullProvider" class="wizard-panel" :class="{ active: currentStep === 2 }">
          <!-- Coleta de Lead: mesmo mapeamento do Sheets, com os campos da coleta -->
          <div v-if="isLeadCollection" class="panel-section">
            <div class="panel-section-title">
              <i class="bx bx-transfer" style="font-size: 15px;"></i> Campos da Coleta
            </div>
            <p class="panel-hint">
              Escolha os campos que vão para a planilha — participante, Controle de
              Inscrição, formulário de coleta e meta (operador/data). O
              <strong>ID da coleta</strong> é sempre a 1ª coluna, automaticamente.
              Se nenhum campo for escolhido, as colunas são montadas automaticamente
              (modo antigo).
            </p>
            <div v-if="loadingLeadColumns" class="panel-hint">Carregando campos...</div>
            <FieldMappingTable
              v-else
              v-model="form.field_mappings"
              :availableFields="leadAvailableFields"
              :targetFields="null"
              :fixedTarget="false"
            />
          </div>

          <div v-if="!isLeadCollection" class="panel-section">
            <div class="panel-section-title">
              <i class="bx bx-transfer" style="font-size: 15px;"></i> Mapeamento de Campos
            </div>
            <p class="panel-hint">{{ fixedTarget ? 'De onde vem no participante → campo no destino.' : 'De onde vem → pra onde vai na planilha.' }}</p>
            <FieldMappingTable
              v-model="form.field_mappings"
              :availableFields="availableFields"
              :targetFields="targetFields"
              :fixedTarget="fixedTarget"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="wizard-modal-footer">
        <button class="wz-btn wz-btn-outline" @click="onPrev">
          <i class="bx" :class="currentStep === 0 ? 'bx-x' : 'bx-left-arrow-alt'" style="font-size: 16px;"></i>
          {{ currentStep === 0 ? 'Cancelar' : 'Voltar' }}
        </button>
        <button
          v-if="currentStep < activeSteps.length - 1"
          class="wz-btn wz-btn-primary"
          @click="onNext"
        >
          Próximo
          <i class="bx bx-right-arrow-alt" style="font-size: 16px;"></i>
        </button>
        <button
          v-else
          class="wz-btn wz-btn-success"
          :disabled="saving"
          @click="onSave"
        >
          <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-check'" style="font-size: 16px;"></i>
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { errorMessage } from '@/components/alertsMessages'
import FieldMappingTable from './FieldMappingTable.vue'
import groupService from '@/pages/groups/services'
import integrationService from '../services'

export default {
  name: 'WizardModal',
  components: { FieldMappingTable },
  props: {
    show: { type: Boolean, default: false },
    form: { type: Object, required: true },
    providers: { type: Array, default: () => [] },
    availableFields: { type: Array, default: () => [] },
    isEditing: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    testing: { type: Boolean, default: false },
    testResult: { default: null },
    testError: { type: String, default: '' }
  },
  data () {
    return {
      currentStep: 0,
      steps: ['Configuração', 'Filtros', 'Mapeamento'],
      freqType: 'manual',
      freqValue: 15,
      freqTime: '08:00',
      statusOptions: [
        { value: 0, label: 'A Definir', color: '#b8bdd0' },
        { value: 1, label: 'Em Andamento', color: '#2563eb' },
        { value: 2, label: 'Pendente', color: '#f1b44c' },
        { value: 3, label: 'Confirmado', color: '#34c38f' },
        { value: 4, label: 'Declinado', color: '#f46a6a' },
        { value: 5, label: 'Inativo', color: '#74788d' },
        { value: 99, label: 'Convidado', color: '#7c3aed' }
      ],
      providerMeta: {
        google_sheets: {
          name: 'Google Sheets',
          logo: 'https://upload.wikimedia.org/wikipedia/commons/a/ae/Google_Sheets_2020_Logo.svg'
        },
        sympla: {
          name: 'Sympla',
          logo: null,
          icon: 'mdi mdi-alpha-s-box'
        },
        whatsapp_positus: {
          name: 'WhatsApp',
          logo: null,
          icon: 'bx bxl-whatsapp'
        },
        eventfy_app: {
          name: 'App Eventfy',
          logo: null,
          icon: 'bx bx-mobile-alt'
        }
      },
      groups: [],
      leadColumns: [],
      leadAvailableFields: [],
      loadingLeadColumns: false
    }
  },
  created () {
    this.loadGroups()
  },
  computed: {
    selectedProvider () {
      return this.providers.find(p => p.key === this.form.provider) || null
    },
    providerLogo () {
      const meta = this.providerMeta[this.form.provider]
      return meta ? meta.logo : null
    },
    isPullProvider () {
      return this.form.sync_direction === 'pull'
    },
    // Coleta de Lead: as colunas são montadas pelo backend (informações do
    // participante + respostas do formulário + operador e data), então não há
    // mapeamento manual e os filtros são por período da coleta.
    isLeadCollection () {
      return this.form.provider === 'lead_collection'
    },
    activeSteps () {
      // Pull providers don't need filters and field mapping steps
      if (this.isPullProvider) return ['Configuração']
      if (this.isLeadCollection) return ['Configuração', 'Filtros', 'Campos']
      return ['Configuração', 'Filtros', 'Mapeamento']
    },
    inputConfigFields () {
      if (!this.selectedProvider) return []
      return this.selectedProvider.config_fields.filter(f => f.type !== 'boolean')
    },
    boolConfigFields () {
      if (!this.selectedProvider) return []
      return this.selectedProvider.config_fields.filter(f => f.type === 'boolean')
    },
    credentialFields () {
      if (!this.selectedProvider) return []
      return this.selectedProvider.credential_fields || []
    },
    targetFields () {
      return this.selectedProvider ? (this.selectedProvider.target_fields || null) : null
    },
    fixedTarget () {
      return !!(this.selectedProvider && this.selectedProvider.fixed_target)
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.currentStep = 0
        this.parseFrequency(this.form.sync_frequency)
        this.ensureCredentials()
        this.applyConfigDefaults()
        if (this.isLeadCollection) {
          this.loadLeadColumns()
        }
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    'form.provider' () {
      this.applyConfigDefaults()
      if (this.isLeadCollection) {
        this.loadLeadColumns()
      }
    }
  },
  methods: {
    // Garante que form.credentials seja um objeto antes do v-model dos
    // credential_fields (vem null da API quando não há credenciais).
    ensureCredentials () {
      if (this.credentialFields.length && (!this.form.credentials || typeof this.form.credentials !== 'object')) {
        this.$set(this.form, 'credentials', {})
      }
    },
    // Preenche os defaults declarados no metadata do provider (ex: sync_mode
    // do Google Sheets) sem sobrescrever valores já salvos na integração.
    applyConfigDefaults () {
      if (!this.selectedProvider || !this.form.config) return
      for (const f of this.selectedProvider.config_fields) {
        const current = this.form.config[f.key]
        if (f.default !== undefined && (current === undefined || current === null || current === '')) {
          this.$set(this.form.config, f.key, f.default)
        }
      }
    },
    goToStep (idx) {
      this.currentStep = idx
    },
    onNext () {
      if (this.currentStep === 0) {
        if (!this.form.name) {
          errorMessage('Informe o nome da integração.')
          return
        }
        if (this.selectedProvider) {
          const required = this.selectedProvider.config_fields.filter(f => f.required)
          for (const f of required) {
            if (!this.form.config[f.key]) {
              errorMessage(`O campo "${f.label}" é obrigatório.`)
              return
            }
          }
          const reqCreds = this.credentialFields.filter(f => f.required)
          for (const f of reqCreds) {
            if (!this.form.credentials || !this.form.credentials[f.key]) {
              errorMessage(`O campo "${f.label}" é obrigatório.`)
              return
            }
          }
        }
      }
      if (this.currentStep < this.activeSteps.length - 1) {
        this.currentStep++
      }
    },
    onPrev () {
      if (this.currentStep > 0) {
        this.currentStep--
      } else {
        this.$emit('close')
      }
    },
    onSave () {
      if (!this.form.name) {
        errorMessage('Informe o nome da integração.')
        this.currentStep = 0
        return
      }
      this.$emit('save')
    },
    toggleStatus (value) {
      const idx = this.form.filters.statuses.indexOf(value)
      if (idx >= 0) {
        this.form.filters.statuses.splice(idx, 1)
      } else {
        this.form.filters.statuses.push(value)
      }
    },
    // Preset "Base completa": marca todos os status (convidados + inscritos) — os 385.
    selectAllStatuses () {
      this.form.filters.statuses = this.statusOptions.map(s => s.value)
    },
    clearStatuses () {
      this.form.filters.statuses = []
    },
    toggleGroup (groupId) {
      if (!this.form.filters.group_ids) {
        this.$set(this.form.filters, 'group_ids', [])
      }
      const idx = this.form.filters.group_ids.indexOf(groupId)
      if (idx >= 0) {
        this.form.filters.group_ids.splice(idx, 1)
      } else {
        this.form.filters.group_ids.push(groupId)
      }
    },
    async loadGroups () {
      try {
        const res = await groupService.list()
        this.groups = res.data.data || []
      } catch (e) {
        this.groups = []
      }
    },
    async loadLeadColumns () {
      this.loadingLeadColumns = true
      try {
        const res = await integrationService.getAvailableFields('lead_collection')
        // Categorias selecionáveis (participante + RC + formulário de coleta + meta).
        this.leadAvailableFields = (res.data && res.data.data) || []
        // Prévia do modo automático (fallback quando não há campos escolhidos).
        this.leadColumns = (res.data && res.data.auto_columns) || []
      } catch (e) {
        this.leadAvailableFields = []
        this.leadColumns = []
        errorMessage('Não foi possível carregar os campos da coleta de lead.')
      } finally {
        this.loadingLeadColumns = false
      }
    },
    parseFrequency (freq) {
      if (!freq || freq === 'manual') {
        this.freqType = 'manual'
        return
      }
      // daily:{HH:MM} or legacy "daily"
      if (freq === 'daily') {
        this.freqType = 'daily'
        this.freqTime = '00:00'
        return
      }
      if (freq.startsWith('daily:')) {
        this.freqType = 'daily'
        this.freqTime = freq.substring(6)
        return
      }
      // legacy "hourly"
      if (freq === 'hourly') {
        this.freqType = 'hours'
        this.freqValue = 1
        return
      }
      // {N}h
      const hMatch = freq.match(/^(\d+)h$/)
      if (hMatch) {
        this.freqType = 'hours'
        this.freqValue = parseInt(hMatch[1])
        return
      }
      // {N}min
      const mMatch = freq.match(/^(\d+)min$/)
      if (mMatch) {
        this.freqType = 'minutes'
        this.freqValue = parseInt(mMatch[1])
        return
      }
      this.freqType = 'manual'
    },
    buildFrequency () {
      if (this.freqType === 'manual') {
        this.form.sync_frequency = 'manual'
      } else if (this.freqType === 'minutes') {
        const val = Math.max(1, Math.min(59, this.freqValue || 15))
        this.form.sync_frequency = val + 'min'
      } else if (this.freqType === 'hours') {
        const val = Math.max(1, Math.min(23, this.freqValue || 1))
        this.form.sync_frequency = val + 'h'
      } else if (this.freqType === 'daily') {
        this.form.sync_frequency = 'daily:' + (this.freqTime || '08:00')
      }
    },
    onFreqTypeChange () {
      if (this.freqType === 'minutes') this.freqValue = 15
      if (this.freqType === 'hours') this.freqValue = 1
      if (this.freqType === 'daily') this.freqTime = '08:00'
      this.buildFrequency()
    }
  },
  beforeDestroy () {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.wizard-overlay {
  display: none;
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(15, 17, 27, 0.45);
  backdrop-filter: blur(4px);
  z-index: 1060;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.wizard-overlay.open {
  display: flex;
  animation: overlayIn 0.25s ease-out;
}
@keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

.wizard-modal {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes modalIn {
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.wizard-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #edf0f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.wizard-modal-title {
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.wizard-modal-close {
  width: 32px; height: 32px;
  border-radius: 4px;
  border: none;
  background: #f8f9fb;
  color: #8d92a9;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}
.wizard-modal-close:hover { background: #f1f3f7; color: #1e2235; }

/* Steps Bar */
.wizard-steps-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  border-bottom: 1px solid #edf0f5;
  background: #f8f9fb;
  gap: 0;
}
.ws-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #8d92a9;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex: 1;
  max-width: 200px;
}
.ws-item:hover { color: #4d5269; }
.ws-item.active { background: #eff4ff; color: #2563eb; font-weight: 600; }
.ws-item.completed { color: #34c38f; }
.ws-num {
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  background: #fff;
  border: 1.5px solid #e2e5ed;
}
.ws-item.active .ws-num { background: #2563eb; color: #fff; border-color: #2563eb; }
.ws-item.completed .ws-num { background: #d4f5e7; color: #34c38f; border-color: #a7f3d0; }
.ws-connector {
  flex: 1;
  max-width: 60px;
  height: 1px;
  background: #e2e5ed;
  flex-shrink: 0;
}
.ws-connector.done { background: #a7f3d0; }

/* Body */
.wizard-modal-body {
  flex: 1;
  overflow-y: auto;
}
.wizard-panel {
  display: none;
  padding: 24px;
}
.wizard-panel.active {
  display: block;
  animation: panelIn 0.2s ease-out;
}
@keyframes panelIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
}

/* Footer */
.wizard-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #edf0f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fb;
}

/* Panel sections */
.panel-section { margin-bottom: 22px; }
.panel-section:last-child { margin-bottom: 0; }
.panel-section-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #1e2235;
}
.panel-section-title i { color: #8d92a9; }
.panel-hint {
  font-size: 12px;
  color: #8d92a9;
  margin-bottom: 12px;
}

/* Colunas automáticas da Coleta de Lead */
.lead-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
}
.lead-column-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #f6f7fb;
  border: 1px solid #e6e8f0;
  border-radius: 6px;
  font-size: 12px;
  color: #4b5066;
}
.lead-column-order {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #e6e8f0;
  color: #8d92a9;
  font-size: 10px;
  font-weight: 600;
}

/* Form */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.form-label {
  font-size: 12px;
  font-weight: 500;
  color: #4d5269;
}
.form-input, .form-select {
  padding: 9px 13px;
  border: 1px solid #e2e5ed;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  color: #1e2235;
  background: #fff;
  transition: all 0.15s;
  outline: none;
  width: 100%;
}
.form-input:focus, .form-select:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px #eff4ff;
}
.form-input::placeholder { color: #b8bdd0; }
.form-input.mono { font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 12px; }
.form-hint { font-size: 11px; color: #8d92a9; }

/* Frequency detail row */
.freq-detail-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f9fb;
  border: 1px solid #edf0f5;
  border-radius: 6px;
  margin-bottom: 14px;
}
.freq-label {
  font-size: 12px;
  color: #4d5269;
  white-space: nowrap;
}
.freq-number {
  width: 64px !important;
  text-align: center;
  flex-shrink: 0;
  padding: 7px 8px !important;
}
.freq-time {
  width: 110px !important;
  flex-shrink: 0;
  padding: 7px 8px !important;
}

/* Toggle */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
  display: inline-block;
}
.toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #e2e5ed;
  border-radius: 100px;
  transition: 0.25s;
}
.toggle-slider::before {
  content: '';
  position: absolute;
  height: 16px; width: 16px;
  left: 3px; bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: 0.25s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.toggle-switch input:checked + .toggle-slider { background: #34c38f; }
.toggle-switch input:checked + .toggle-slider::before { transform: translateX(18px); }

/* Chips */
.chip-group { display: flex; flex-wrap: wrap; gap: 8px; }
.chip-presets { display: flex; gap: 8px; margin-bottom: 8px; }
.chip-preset { border: 1px solid #556ee6; color: #556ee6; background: #eef2ff; border-radius: 16px; padding: 4px 12px; font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
.chip-preset:hover { background: #556ee6; color: #fff; }
.chip-preset-clear { border-color: #cdd7ea; color: #5b6b85; background: #fff; }
.chip-preset-clear:hover { background: #eef2f7; color: #5b6b85; }
.chip {
  padding: 7px 14px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  border: 1.5px solid #e2e5ed;
  background: #fff;
  color: #4d5269;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
  user-select: none;
}
.chip:hover { border-color: #d0d4e0; background: #f8f9fb; }
.chip.selected { border-color: #bfdbfe; background: #eff4ff; color: #2563eb; }
.chip-dot { width: 7px; height: 7px; border-radius: 50%; }
.chip-check {
  width: 15px; height: 15px;
  border-radius: 4px;
  border: 1.5px solid #e2e5ed;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: transparent;
}
.chip.selected .chip-check { background: #2563eb; border-color: #2563eb; color: #fff; }

/* Test Connection */
.test-connection {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #d4f5e7;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
}
.test-connection-text { font-size: 13px; font-weight: 500; color: #0d7a5f; }
.test-connection-sub { font-size: 11px; color: #34a87a; }
.test-error-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fde8e8;
  border: 1px solid #fecaca;
  border-radius: 6px;
}
.test-error-text { font-size: 13px; font-weight: 500; color: #b91c1c; }
.test-error-sub { font-size: 11px; color: #991b1b; }

/* Wizard Buttons (prefixed to avoid Bootstrap conflicts) */
.wz-btn {
  padding: 8px 18px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
}
.wz-btn-sm { padding: 6px 14px; font-size: 12px; }
.wz-btn-primary { background: #2563eb; color: #fff; }
.wz-btn-primary:hover { background: #1d4ed8; }
.wz-btn-success { background: #34c38f; color: #fff; }
.wz-btn-success:hover { background: #2ab07d; }
.wz-btn-outline {
  background: #fff;
  color: #4d5269;
  border: 1px solid #e2e5ed;
}
.wz-btn-outline:hover { background: #f8f9fb; border-color: #d0d4e0; }
.wz-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.text-danger { color: #f46a6a; }

@media (max-width: 600px) {
  .wizard-overlay { padding: 12px; }
  .wizard-modal { max-height: 95vh; }
  .form-row { grid-template-columns: 1fr; }
  .wizard-steps-bar { overflow-x: auto; }
}
</style>
